import { useTranslation } from "react-i18next";
import { QUESTIONNAIRE_STATUSES, showErrorToast, showSuccessToast } from "../../../../utils";
import { Box, Button, chakra, Divider, Flex, Heading, useToast } from "@chakra-ui/react";
import { EsgLabel, FormInput } from "../../../../components/Forms";
import { useForm } from "react-hook-form";
import { useCreateProduct, useUpdateProduct } from "../../../../hooks/query/supplier-questionnaire/product";
import {
  supplierQuestionnaireKeys,
  useDefaultSupplierQuestionnaireConfig,
  useFinalizeSupplierQuestionnaire,
  useGetSupplierQuestionnaireStructure, useUpdateFinancialYearEndMonth
} from "../../../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { NavigationTabs } from "../../../../components/NavigationTabs";
import { mapProductsData } from "./helpers";
import { Fragment, useEffect } from "react";
import { LABEL_TYPES, OTHER_OPTION } from "../FinancialYearEndMonthSelector/FinancialYearEndMonthSelector";
import { FinancialYearEndMonthSelector } from "../FinancialYearEndMonthSelector";
import * as React from "react";
import { Answer } from "../../../SupplierPhysicalRiskQuestionnaire/components/PhysicalRiskAssessmentForm/components";

function Product({ data, returnToListRequested, declineReturnToListRequest, returnToList, isPreview }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierQuestionnaire.product"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const financialYearFormId = "prod-questionnaire-financial-year-form";

  const financialYearFormDefaultValues = {
    financialYearEndMonth: data?.financialYearEndMonth?.month,
    otherClarification: data?.financialYearEndMonth?.otherClarification
  };

  const handleError = (err) => {
    let errorMessage = err.message;
    if (err.response?.status === 409 && err.response?.data?.parameters?.resourceName === "SupplierQuestionnaireProduct") {
      errorMessage = t("duplicatedProductNameMsg");
    }
    showErrorToast(toast, errorMessage);
  };

  const {
    data: structure
  } = useGetSupplierQuestionnaireStructure({
    onError: err => handleError(err)
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetCreateProductForm
  } = useForm({});

  const {
    register: financialYearRegister,
    handleSubmit: handleFinancialYearSubmit,
    formState: financialYearFormState,
    setValue,
    watch
  } = useForm({
    defaultValues: financialYearFormDefaultValues
  });

  const financialYearEndMonthValue = watch("financialYearEndMonth");
  const otherClarificationValue = watch("otherClarification");

  const {
    mutate: finalize,
    isLoading: isLoadingOnFinalize
  } = useFinalizeSupplierQuestionnaire({
    ...useDefaultSupplierQuestionnaireConfig(),
    onSuccess: () => {
      showSuccessToast(toast, globalTranslation("supplierQuestionnaire.page.finalized"));
      returnToList();
    }
  });

  const createProductQueryConfig = {
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      queryClient.invalidateQueries(supplierQuestionnaireKeys.supplierQuestionnaireById());
      resetCreateProductForm();
    },
    onError: err => handleError(err),
    onSettled: () => dispatch(hideAppSpinner())
  };

  const { mutate: createProduct } = useCreateProduct(createProductQueryConfig);
  const { mutate: updateProduct, isLoading: isLoadingOnUpdate } = useUpdateProduct();

  useEffect(() => {
    if (isLoadingOnUpdate || isLoadingOnFinalize) {
      dispatch(showAppSpinner());
    }
  }, [dispatch, isLoadingOnUpdate, isLoadingOnFinalize]);

  const onSubmitCreateProduct = (submitData) => {
    createProduct({ questionnaireId: data.id, data: { description: submitData.productDescription } });
  };

  const isFinancialYearFormValid = () => {
    if (financialYearEndMonthValue !== null && typeof financialYearEndMonthValue !== "undefined" &&
      financialYearEndMonthValue !== "") {
      if (financialYearEndMonthValue !== OTHER_OPTION) {
        return true;
      } else {
        if (otherClarificationValue !== null && typeof otherClarificationValue !== "undefined" &&
          otherClarificationValue !== "") {
          return true;
        }
      }
    }
    return false;
  };

  const saveMethod = (productData, isFinalize) => {
    const financialYearForm = document.getElementById(financialYearFormId);
    if (financialYearForm) {
      financialYearForm.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
    }

    if (isFinancialYearFormValid()) {
      if (productData) {
        updateProduct({
          questionnaireId: data.id,
          id: productData.id,
          data: productData
        }, {
          onSuccess: () => {
            if (isFinalize) {
              finalize(data.id);
            } else {
              showSuccessToast(toast, t("productSaved", { productName: productData.description }));
              if (returnToListRequested === true) {
                returnToList();
              }
              queryClient.invalidateQueries(supplierQuestionnaireKeys.supplierQuestionnaireById());
            }
          },
          onError: err => handleError(err),
          onSettled: () => dispatch(hideAppSpinner())
        });
      } else {
        if (isFinalize) {
          finalize(data.id);
        } else if (returnToListRequested === true) {
          returnToList();
        }
      }
    }
  };

  const { mutate: updateFinancialYearEndMonth } = useUpdateFinancialYearEndMonth({
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      showSuccessToast(toast, globalTranslation("supplierQuestionnaire.page.financialYearEndSetSuccess"));
      queryClient.invalidateQueries(supplierQuestionnaireKeys.supplierQuestionnaireById());
    },
    onError: err => showErrorToast(toast, err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const onFinancialYearFormSubmit = (formData) => {
    if (formData.financialYearEndMonth !== financialYearFormDefaultValues?.financialYearEndMonth ||
      (formData.financialYearEndMonth === OTHER_OPTION &&
        formData.otherClarification !== financialYearFormDefaultValues?.otherClarification)
    ) {
      updateFinancialYearEndMonth({
        questionnaireId: data?.id,
        body: {
          month: formData.financialYearEndMonth,
          otherClarification: formData.otherClarification
        }
      });
    }
  };

  return (
    <Fragment>
      {(data.status === QUESTIONNAIRE_STATUSES.NEW || data.status === QUESTIONNAIRE_STATUSES.IN_PROGRESS) && !isPreview ?
        <Box>
          <chakra.form id="addProduct" onSubmit={handleSubmit(onSubmitCreateProduct)}>
            <Flex gap="4" marginBlockStart="5" alignItems="baseline">
              <FormInput
                placeholder={t("inputs.productDescriptionPlaceholder")}
                errors={errors}
                {...register("productDescription", {
                  required: globalTranslation("entityRequired", { entityName: t("inputs.productDescriptionPlaceholder") })
                })}
              />
              <Button
                colorScheme="customScheme"
                width="32"
                type="submit"
                title={t("buttons.addProduct")}
              >
                {t("buttons.addProduct")}
              </Button>
            </Flex>
          </chakra.form>
        </Box> : null
      }
      {structure && data.products && data.products.length > 0 ?
        (data.status === QUESTIONNAIRE_STATUSES.NEW || data.status === QUESTIONNAIRE_STATUSES.IN_PROGRESS) && !isPreview ?
          <Flex flexFlow="column" gap={5}>
            <Divider marginBlockStart={5} />
            <chakra.form
              id={financialYearFormId}
              onSubmit={handleFinancialYearSubmit(onFinancialYearFormSubmit)}
            >
              <FinancialYearEndMonthSelector
                labelType={LABEL_TYPES.STANDARD}
                formState={financialYearFormState}
                register={financialYearRegister}
                watch={watch}
                setValue={setValue}
              />
            </chakra.form>
            <Divider marginBlockEnd={5} />
            <NavigationTabs
              elements={mapProductsData(data.products, data.id, structure)}
              tabTitle={null}
              subTitleAttribute={null}
              saveMethod={saveMethod}
              formSubmission={{
                allowed: true,
                requested: returnToListRequested,
                decline: declineReturnToListRequest
              }}
            />
          </Flex> :
          <Flex flexFlow="column" gap={5}>
            <Box>
              <EsgLabel text={`${globalTranslation("supplierQuestionnaire.page.financialYearEnd")} *`} />
              <Answer
                answerText={`${financialYearFormDefaultValues?.financialYearEndMonth !== OTHER_OPTION ?
                  financialYearFormDefaultValues?.financialYearEndMonth ?? "" :
                  financialYearFormDefaultValues?.otherClarification ?? ""}`} />
            </Box>
            <Divider marginBlockEnd={5} />
            <NavigationTabs
              elements={mapProductsData(data.products, data.id, structure)}
              tabTitle={null}
              subTitleAttribute={null}
            />
          </Flex> :
        <Heading size="md" marginBlockStart="4">
          {t("missingProducts")}
        </Heading>
      }
    </Fragment>
  );
}

export default Product;